import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Trash2, Upload, X, FileText, Download } from 'lucide-react';
import { productsAPI } from '../../services/api';
import './AdminSections.css';

const ProductManagement = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    images: [],
    code: '',
    technical_specs: '',
    advantages: '',
    name_en: '',
    description_en: '',
    technical_specs_en: '',
    seo_keywords: '',
    seo_keywords_en: ''
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadProducts(selectedCategory.id);
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await productsAPI.getCategories();
      setCategories(response.data);
      if (response.data.length > 0) {
        setSelectedCategory(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async (categoryId) => {
    try {
      const response = await productsAPI.getByCategory(categoryId);
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const result = await productsAPI.uploadImage(file);
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${result.url}`);
      }
      
      if (!formData.image_url && uploaded.length > 0) {
        setFormData(prev => ({
          ...prev,
          image_url: uploaded[0],
          images: [...prev.images, ...uploaded]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...uploaded]
        }));
      }
    } catch (error) {
      alert('Σφάλμα κατά τη μεταφόρτωση');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    const newImages = formData.images.filter((_, i) => i !== idx);
    const newMainImage = newImages.length > 0 ? newImages[0] : '';
    setFormData(prev => ({ ...prev, images: newImages, image_url: newMainImage }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, formData);
      } else {
        await productsAPI.create({
          ...formData,
          category_id: selectedCategory.id
        });
      }
      
      setShowModal(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', image_url: '', images: [], code: '', technical_specs: '', advantages: '', name_en: '', description_en: '', technical_specs_en: '', seo_keywords: '', seo_keywords_en: '' });
      loadProducts(selectedCategory.id);
    } catch (error) {
      alert('Σφάλμα: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το προϊόν;')) return;

    try {
      await productsAPI.delete(productId);
      loadProducts(selectedCategory.id);
    } catch (error) {
      alert('Σφάλμα κατά τη διαγραφή');
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      image_url: product.image_url || '',
      images: product.images || [],
      code: product.code || '',
      technical_specs: product.technical_specs || '',
      advantages: product.advantages || '',
      name_en: product.name_en || '',
      description_en: product.description_en || '',
      technical_specs_en: product.technical_specs_en || '',
      seo_keywords: product.seo_keywords || '',
      seo_keywords_en: product.seo_keywords_en || ''
    });
    setCertifications(product.certifications || []);
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', image_url: '', images: [], code: '', technical_specs: '', advantages: '', name_en: '', description_en: '', technical_specs_en: '', seo_keywords: '', seo_keywords_en: '' });
    setCertifications([]);
    setShowModal(true);
  };

  const handleCertUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (!editingProduct) {
      alert('Παρακαλώ αποθηκεύστε πρώτα το προϊόν, μετά μπορείτε να προσθέσετε πιστοποιήσεις.');
      e.target.value = '';
      return;
    }

    setUploadingCert(true);
    try {
      for (const file of files) {
        const res = await productsAPI.uploadCertification(editingProduct.id, file);
        setCertifications(prev => [...prev, res.data]);
      }
    } catch (err) {
      alert('Σφάλμα κατά τη μεταφόρτωση: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingCert(false);
      e.target.value = '';
    }
  };

  const handleCertDelete = async (certId) => {
    if (!editingProduct) return;
    if (!window.confirm('Διαγραφή αυτής της πιστοποίησης;')) return;
    try {
      await productsAPI.deleteCertification(editingProduct.id, certId);
      setCertifications(prev => prev.filter(c => c.id !== certId));
    } catch (err) {
      alert('Σφάλμα κατά τη διαγραφή');
    }
  };

  const getCertUrl = (path) => {
    if (!path) return '#';
    if (path.startsWith('http')) return path;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Package size={24} />
          Διαχείριση Προϊόντων
        </h2>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={18} />
          Προσθήκη Προϊόντος
        </button>
      </div>

      <div className="categories-tabs">
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`category-tab ${selectedCategory?.id === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat.title}
          </button>
        ))}
      </div>

      <div className="products-list">
        {products.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <p>Δεν υπάρχουν προϊόντα σε αυτή την κατηγορία</p>
            <button className="btn-secondary" onClick={openAddModal}>
              Προσθήκη Πρώτου Προϊόντος
            </button>
          </div>
        ) : (
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-item">
                <div className="product-image-container">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} />
                  ) : (
                    <div className="no-image">
                      <Package size={32} />
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <h4>{product.name}</h4>
                  <p>{product.description}</p>
                  {(product.images?.length > 0 || product.image_url) && (
                    <p style={{ fontSize: '0.8rem', color: '#999', margin: '0.25rem 0' }}>
                      {(product.images?.length || (product.image_url ? 1 : 0))} εικόνες
                    </p>
                  )}
                  <div className="product-actions">
                    <button className="btn-icon" onClick={() => openEditModal(product)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="btn-icon danger" onClick={() => handleDelete(product.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay-admin" onClick={() => setShowModal(false)}>
          <div className="modal-admin" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h3>{editingProduct ? 'Επεξεργασία' : 'Προσθήκη'} Προϊόντος</h3>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <div className="form-group-admin">
                <label>Όνομα Προϊόντος *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name"
                />
              </div>
              <div className="form-group-admin">
                <label>🇬🇧 Product Name (English)</label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  placeholder="Product name in English"
                  data-testid="product-name-en"
                />
              </div>
              <div className="form-group-admin">
                <label>Κωδικός Προϊόντος</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="π.χ. ESS-34-HYBRID"
                  data-testid="product-code"
                />
              </div>
              <div className="form-group-admin">
                <label>Περιγραφή *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  required
                  placeholder="Σύντομη περιγραφή του προϊόντος..."
                  data-testid="product-description"
                />
              </div>
              <div className="form-group-admin">
                <label>🇬🇧 Description (English)</label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  rows="4"
                  placeholder="Product description in English..."
                  data-testid="product-description-en"
                />
              </div>
              <div className="form-group-admin">
                <label>Τεχνικά Χαρακτηριστικά</label>
                <textarea
                  value={formData.technical_specs}
                  onChange={(e) => setFormData({ ...formData, technical_specs: e.target.value })}
                  rows="5"
                  placeholder="Μία ιδιότητα ανά γραμμή..."
                  data-testid="product-technical"
                />
              </div>
              <div className="form-group-admin">
                <label>🇬🇧 Technical Specifications (English)</label>
                <textarea
                  value={formData.technical_specs_en}
                  onChange={(e) => setFormData({ ...formData, technical_specs_en: e.target.value })}
                  rows="5"
                  placeholder="One specification per line..."
                  data-testid="product-technical-en"
                />
              </div>

              <div style={{ padding: '1rem 1.25rem', background: '#f5f5ff', border: '1px solid #d0d0ff', margin: '0.5rem 0' }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#667eea', fontWeight: 600, letterSpacing: '0.05em' }}>
                  🔍 SEO Λέξεις-Κλειδιά
                </h4>
                <div className="form-group-admin" style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.85rem' }}>Ελληνικά — χωρισμός με κόμμα</label>
                  <input
                    type="text"
                    value={formData.seo_keywords}
                    onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                    placeholder="π.χ. συρόμενο κούφωμα, αλουμίνιο, θερμομόνωση, ..."
                    data-testid="product-seo-keywords"
                  />
                </div>
                <div className="form-group-admin" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.85rem' }}>🇬🇧 SEO Keywords (English)</label>
                  <input
                    type="text"
                    value={formData.seo_keywords_en}
                    onChange={(e) => setFormData({ ...formData, seo_keywords_en: e.target.value })}
                    placeholder="e.g. sliding frame, aluminum, thermal insulation, ..."
                    data-testid="product-seo-keywords-en"
                  />
                </div>
              </div>
              <div className="form-group-admin">
                <label>Πιστοποιήσεις (αρχεία PDF, εικόνες κ.λπ.)</label>
                {!editingProduct && (
                  <p style={{ fontSize: '0.8rem', color: '#999', margin: '0 0 0.5rem' }}>
                    ⓘ Αποθηκεύστε πρώτα το προϊόν, μετά μπορείτε να επισυνάψετε αρχεία πιστοποίησης.
                  </p>
                )}
                {editingProduct && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      multiple
                      onChange={handleCertUpload}
                      id="cert-upload"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="cert-upload" className="upload-button" style={{ opacity: uploadingCert ? 0.6 : 1 }}>
                      <Upload size={18} />
                      {uploadingCert ? 'Μεταφόρτωση...' : 'Επιλογή Αρχείων Πιστοποίησης'}
                    </label>

                    {certifications.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {certifications.map(cert => (
                          <div key={cert.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.65rem 0.85rem', border: '1px solid #e0e0e0', background: '#fafafa'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1, minWidth: 0 }}>
                              <FileText size={18} color="#667eea" />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {cert.filename}
                                </p>
                                <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>
                                  {(cert.file_size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                              <a href={getCertUrl(cert.file_path)} target="_blank" rel="noreferrer" className="btn-icon" style={{ textDecoration: 'none' }}>
                                <Download size={14} />
                              </a>
                              <button type="button" className="btn-icon danger" onClick={() => handleCertDelete(cert.id)}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group-admin">
                <label>Εικόνες (πολλαπλή επιλογή)</label>
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    id="image-upload"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="image-upload" className="upload-button">
                    <Upload size={18} />
                    {uploading ? 'Μεταφόρτωση...' : 'Επιλογή Εικόνων'}
                  </label>
                  {formData.images.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                      {formData.images.map((img, idx) => (
                        <div key={idx} style={{ position: 'relative', width: 80, height: 80 }}>
                          <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', border: '1px solid #e0e0e0' }} />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            style={{
                              position: 'absolute', top: -6, right: -6,
                              width: 20, height: 20, borderRadius: '50%',
                              background: '#d32f2f', border: 'none', color: '#fff',
                              cursor: 'pointer', fontSize: '12px', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', padding: 0
                            }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Ακύρωση
                </button>
                <button type="submit" className="btn-primary" disabled={loading || uploading}>
                  {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;